import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Link,
  Button,
  TextField,
} from "@mui/material";
import {
  CheckCircle,
  Circle,
  Clock,
  MessageCircle,
  ChevronDown,
  ChevronUp,
  ArrowUp,
} from "lucide-react";
import StreamingTextBox from "components/common/StreamingTextBox";
import WithDividers from "components/helpers/WithDividers";
import {
  FunctionCall,
  TodoItem,
  BookmarkItem,
  UserQuestion,
} from "components/hooks/useTaskAgentState";
import QuestionAnswerInput from "./QuestionAnswerInput";
import Duration from "components/common/Duration";

// XML Component mapping
const XML_COMPONENT_MAP = {
  Table: "ChatMessageTable",
  b: "bold",
  i: "italic",
  h1: "heading1",
  h2: "heading2",
  quote: "quote",
  a: "link",
  // Add more XML components here as needed
};

// XML Component renderers
const XmlBold = ({ children }: { children: React.ReactNode }) => (
  <Typography component="span" sx={{ fontWeight: "bold" }}>
    {children}
  </Typography>
);

const XmlItalic = ({ children }: { children: React.ReactNode }) => (
  <Typography component="span" sx={{ fontStyle: "italic" }}>
    {children}
  </Typography>
);

const XmlHeading1 = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="h4" sx={{ marginY: 2, fontWeight: "bold" }}>
    {children}
  </Typography>
);

const XmlHeading2 = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="h5" sx={{ marginY: 1.5, fontWeight: "bold" }}>
    {children}
  </Typography>
);

const XmlQuote = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      borderLeft: "4px solid #e0e0e0",
      paddingLeft: 2,
      marginY: 1,
      fontStyle: "italic",
      color: "text.secondary",
    }}
  >
    {children}
  </Box>
);

const XmlLink = ({ xmlContent }: { xmlContent: string }) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlContent, "text/xml");
  const link = doc.documentElement;

  const href = link.getAttribute("href");
  const content = link.textContent;
  if (!href) return <>{content}</>;
  return (
    <Link href={href || ""} target="_blank" rel="noopener noreferrer">
      {content}
    </Link>
  );
};

// ChatMessageTable component for rendering XML tables
const ChatMessageTable = ({ xmlContent }: { xmlContent: string }) => {
  const parseTableXML = (xml: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xml, "text/xml");
    const table = doc.querySelector("Table");

    if (!table) return null;

    const title = table.getAttribute("title");
    const rows = Array.from(table.querySelectorAll("row"));

    return {
      title,
      rows: rows.map((row) => {
        const cells = Array.from(row.children);
        return cells.map((cell) => ({
          type: cell.tagName.toLowerCase(),
          content: cell.textContent || "",
          href: cell.getAttribute("href") || cell.getAttribute("url"),
          src: cell.getAttribute("src"),
          title: cell.getAttribute("title"),
        }));
      }),
    };
  };

  const tableData = parseTableXML(xmlContent);

  if (!tableData) return null;

  const renderCell = (cell: any) => {
    switch (cell.type) {
      case "header":
        return (
          <Typography variant="bodyHeavy" sx={{ fontWeight: 600 }}>
            {cell.content}
          </Typography>
        );
      case "link":
        return (
          <Link href={cell.href} target="_blank" rel="noopener noreferrer">
            {cell.title || cell.content}
          </Link>
        );
      case "text":
        return cell.href ? (
          <Link href={cell.href} target="_blank" rel="noopener noreferrer">
            {cell.content}
          </Link>
        ) : (
          <Typography variant="body">{cell.content}</Typography>
        );
      case "img":
        return (
          <Box
            component="img"
            src={cell.src}
            alt=""
            sx={{ maxWidth: "100px", height: "auto" }}
          />
        );
      default:
        return <Typography variant="body">{cell.content}</Typography>;
    }
  };

  return (
    <Box sx={{ margin: "16px 0" }}>
      {tableData.title && (
        <Typography variant="bodyHeavy" sx={{ marginBottom: "8px" }}>
          {tableData.title}
        </Typography>
      )}
      <Table size="small" sx={{ border: "1px solid #e0e0e0" }}>
        <TableHead>
          {tableData.rows[0] && (
            <TableRow>
              {tableData.rows[0].map((cell, index) => (
                <TableCell key={index} sx={{ backgroundColor: "#f5f5f5" }}>
                  {renderCell(cell)}
                </TableCell>
              ))}
            </TableRow>
          )}
        </TableHead>
        <TableBody>
          {tableData.rows.slice(1).map((row, rowIndex) => (
            <TableRow key={rowIndex}>
              {row.map((cell, cellIndex) => (
                <TableCell key={cellIndex}>{renderCell(cell)}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

// Status indicators matching the block patterns
const StatusIcon = ({
  status,
}: {
  status: "pending" | "in_progress" | "completed";
}) => {
  const props = { size: 16 };
  switch (status) {
    case "completed":
      return <CheckCircle {...props} color="#4CAF50" />;
    case "in_progress":
      return <Clock {...props} color="#FF9800" />;
    default:
      return <Circle {...props} color="#9E9E9E" />;
  }
};

const FunctionCallStatusIcon = ({
  state,
}: {
  state: "queued" | "running" | "completed";
}) => {
  // Gentle fade animation keyframes
  const gentleFadeKeyframes = `
    @keyframes gentleFade {
      0%, 100% { opacity: 0.4; }
      50% { opacity: 1; }
    }
  `;

  const dotBaseStyle = {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    display: "inline-block",
  };

  switch (state) {
    case "completed":
      return (
        <Box
          sx={{
            ...dotBaseStyle,
            backgroundColor: "#495057",
          }}
        />
      );
    case "running":
      return (
        <>
          <style>{gentleFadeKeyframes}</style>
          <Box
            sx={{
              ...dotBaseStyle,
              backgroundColor: "#28a745",
              animation: "gentleFade 1.8s infinite ease-in-out",
            }}
          />
        </>
      );
    case "queued":
      return (
        <>
          <style>{gentleFadeKeyframes}</style>
          <Box
            sx={{
              ...dotBaseStyle,
              backgroundColor: "#ffc107",
              animation: "gentleFade 2.5s infinite ease-in-out",
            }}
          />
        </>
      );
    default:
      return (
        <Box
          sx={{
            ...dotBaseStyle,
            backgroundColor: "#495057",
          }}
        />
      );
  }
};

// Individual entry components
const TodoEntry = ({ todo }: { todo: TodoItem }) => {
  return (
    <Box
      display="flex"
      flexDirection="row"
      alignItems="center"
      gap="12px"
      sx={{
        padding: "8px 0",
        opacity: todo.status === "completed" ? 0.7 : 1,
      }}
    >
      <StatusIcon status={todo.status} />
      <Box flex="1">
        <Typography
          variant="body"
          sx={{
            fontWeight: todo.status === "in_progress" ? 600 : 400,
            textDecoration:
              todo.status === "completed" ? "line-through" : "none",
          }}
        >
          {todo.content}
        </Typography>
      </Box>
    </Box>
  );
};

const BookmarkEntry = ({ bookmark }: { bookmark: BookmarkItem }) => {
  const typeEmoji = {
    task: "📋",
    person: "👤",
    document: "📄",
    location: "📍",
    resource: "🔗",
    idea: "💡",
    issue: "⚠️",
  };

  return (
    <Box
      display="flex"
      flexDirection="row"
      alignItems="flex-start"
      gap="12px"
      sx={{
        padding: "8px 0",
      }}
    >
      <Box sx={{ minWidth: "20px", fontSize: "16px" }}>
        {typeEmoji[bookmark.type] || "🔖"}
      </Box>
      <Box flex="1">
        <Box
          display="flex"
          alignItems="center"
          gap="8px"
          sx={{ marginBottom: "4px" }}
        >
          <Typography
            variant="body"
            sx={{
              fontWeight: 600,
              color: "#3D3D3D",
            }}
          >
            {bookmark.title}
          </Typography>
          <Typography
            variant="bodySmall"
            sx={{
              backgroundColor: "#f0f0f0",
              color: "#666",
              padding: "2px 6px",
              borderRadius: "4px",
              fontSize: "0.75rem",
              textTransform: "uppercase",
              fontWeight: 500,
            }}
          >
            {bookmark.type}
          </Typography>
        </Box>
        <Typography
          variant="bodySmall"
          color="text.secondary"
          sx={{ marginBottom: "2px" }}
        >
          {bookmark.reason}
        </Typography>
        <Typography
          variant="bodySmall"
          color="text.secondary"
          sx={{ fontFamily: "monospace", fontSize: "0.7rem" }}
        >
          ID: {bookmark.id}
        </Typography>
      </Box>
    </Box>
  );
};

const FunctionCallEntry = ({ call }: { call: FunctionCall }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const hasDetails = Object.keys(call.args).length > 0 || call.result;

  // Format arguments for inline display
  const formatArgs = (args: Record<string, any>) => {
    const entries = Object.entries(args);
    if (entries.length === 0) return "";

    const formatted = entries
      .map(([key, value]) => {
        const val = String(value);
        return val.length > 30
          ? `${key}: ${val.substring(0, 30)}...`
          : `${key}: ${val}`;
      })
      .join(", ");

    return formatted.length > 80
      ? `${formatted.substring(0, 80)}...`
      : formatted;
  };
  return (
    <Box sx={{ padding: "8px 0" }}>
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        gap="12px"
        sx={{
          cursor: hasDetails ? "pointer" : "default",
        }}
        onClick={() => hasDetails && setIsExpanded(!isExpanded)}
      >
        <FunctionCallStatusIcon state={call.state} />
        <Box flex="1">
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            gap="8px"
            flexWrap="wrap"
          >
            <Typography
              variant="body"
              sx={{
                fontWeight: 600,
                color: "#3D3D3D",
              }}
            >
              {call.name}
            </Typography>
            {Object.keys(call.args).length > 0 && (
              <Typography variant="bodySmall" color="text.secondary">
                ({formatArgs(call.args)})
              </Typography>
            )}
          </Box>
        </Box>
        <Typography
          variant="bodySmall"
          color="text.secondary"
          sx={{ minWidth: "fit-content" }}
        >
          {call.state === "running" && call.startTime && (
            <Duration startTime={new Date(call.startTime)} />
          )}
          {call.state === "completed" &&
            call.endTime &&
            call.startTime &&
            `${((call.endTime - call.startTime) / 1000).toFixed(1)}s`}
          {call.state === "queued" && `queued`}
        </Typography>
        {hasDetails && (
          <IconButton
            size="small"
            sx={{ padding: "4px" }}
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
          >
            {isExpanded ? (
              <ChevronUp size={16} stroke="#8E9598" />
            ) : (
              <ChevronDown size={16} stroke="#8E9598" />
            )}
          </IconButton>
        )}
      </Box>

      {hasDetails && (
        <Collapse in={isExpanded}>
          <Box sx={{ marginTop: "8px", paddingLeft: "28px" }}>
            {Object.keys(call.args).length > 0 && (
              <Typography
                variant="bodySmall"
                color="text.secondary"
                sx={{ marginBottom: "4px" }}
              >
                <strong>Arguments:</strong>{" "}
                {Object.entries(call.args)
                  .map(([key, value]) => `${key}: ${String(value)}`)
                  .join(", ")}
              </Typography>
            )}
            {call.result && (
              <Typography
                variant="bodySmall"
                color="text.primary"
                sx={{
                  backgroundColor: "#f5f5f5",
                  padding: "8px",
                  borderRadius: "4px",
                  fontStyle: "italic",
                }}
              >
                <strong>Result:</strong> {call.result}
              </Typography>
            )}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

const UserQuestionEntry = ({
  question,
  onAnswerQuestion,
}: {
  question: UserQuestion;
  onAnswerQuestion?: (
    answers: { functionId: string; answer: string }[],
  ) => void;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAnswerInput, setShowAnswerInput] = useState(!question.answered);

  const hasDetails = question.options && question.options.length > 0;

  const handleAnswer = (answer: string) => {
    console.log(`Question answered: "${question.question}" -> "${answer}"`);
    setShowAnswerInput(false);
    // Call the API to submit the answer
    if (onAnswerQuestion) {
      onAnswerQuestion([{ functionId: question.id, answer }]);
    }
  };

  const handleSkip = () => {
    console.log(`Question skipped: "${question.question}"`);
    setShowAnswerInput(false);
    // In a real implementation, this would mark the question as skipped
  };

  return (
    <Box sx={{ padding: "8px 0" }}>
      <Box
        display="flex"
        flexDirection="row"
        alignItems="flex-start"
        gap="12px"
        sx={{
          opacity: question.answered ? 0.8 : 1,
          cursor: hasDetails ? "pointer" : "default",
        }}
        onClick={() => hasDetails && setIsExpanded(!isExpanded)}
      >
        <MessageCircle
          size={16}
          color={question.answered ? "#4CAF50" : "#FF9800"}
        />
        <Box flex="1">
          <Typography
            variant="body"
            sx={{
              fontWeight: question.answered ? 400 : 600,
              marginBottom: "4px",
            }}
          >
            {question.question}
          </Typography>

          <Typography variant="bodySmall" color="text.secondary">
            {question.answered ? (
              <span style={{ color: "#4CAF50" }}>✓ Answered</span>
            ) : (
              <span style={{ color: "#FF9800" }}>⏳ Awaiting response</span>
            )}
          </Typography>

          {question.answer && (
            <Typography
              variant="bodySmall"
              sx={{
                backgroundColor: "#e8f5e8",
                padding: "8px",
                borderRadius: "4px",
                marginTop: "8px",
                border: "1px solid #c8e6c9",
              }}
            >
              <strong>Answer:</strong> {question.answer}
            </Typography>
          )}
        </Box>

        {hasDetails && (
          <IconButton
            size="small"
            sx={{ padding: "4px" }}
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
          >
            {isExpanded ? (
              <ChevronUp size={16} stroke="#8E9598" />
            ) : (
              <ChevronDown size={16} stroke="#8E9598" />
            )}
          </IconButton>
        )}
      </Box>

      {hasDetails && (
        <Collapse in={isExpanded}>
          <Box sx={{ marginTop: "8px", paddingLeft: "28px" }}>
            <Typography
              variant="bodySmall"
              color="text.secondary"
              sx={{ marginBottom: "4px" }}
            >
              <strong>Available Options:</strong>
            </Typography>
            <Box sx={{ paddingLeft: "8px" }}>
              {question.options?.map((option, index) => (
                <Typography
                  key={index}
                  variant="bodySmall"
                  color="text.primary"
                  sx={{
                    display: "block",
                    marginBottom: "2px",
                    backgroundColor:
                      question.answer === option ? "#e8f5e8" : "#fafafa",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    border:
                      question.answer === option
                        ? "1px solid #c8e6c9"
                        : "1px solid #e0e0e0",
                  }}
                >
                  {index + 1}. {option}
                  {question.answer === option && (
                    <span style={{ color: "#4CAF50", marginLeft: "8px" }}>
                      ✓ Selected
                    </span>
                  )}
                </Typography>
              ))}
            </Box>
          </Box>
        </Collapse>
      )}

      {/* Show answer input for unanswered questions */}
      {showAnswerInput && !question.answered && (
        <QuestionAnswerInput
          question={question}
          onAnswer={handleAnswer}
          onSkip={handleSkip}
        />
      )}
    </Box>
  );
};

// Section components
export const TodoSection = ({ todos }: { todos: TodoItem[] }) => {
  if (todos.length === 0) return null;

  return (
    <Box>
      <Typography variant="bodyHeavy" sx={{ marginBottom: "8px" }}>
        Task Progress
      </Typography>
      <WithDividers>
        {todos.map((todo) => (
          <TodoEntry key={todo.id} todo={todo} />
        ))}
      </WithDividers>
    </Box>
  );
};

export const BookmarkSection = ({
  bookmarks,
}: {
  bookmarks: BookmarkItem[];
}) => {
  if (bookmarks.length === 0) return null;

  return (
    <Box>
      <Typography variant="bodyHeavy" sx={{ marginBottom: "8px" }}>
        Bookmarks
        <Typography
          component="span"
          variant="bodySmall"
          sx={{
            marginLeft: "8px",
            backgroundColor: "#e3f2fd",
            color: "#1565c0",
            padding: "2px 6px",
            borderRadius: "4px",
            fontSize: "0.75rem",
          }}
        >
          {bookmarks.length} items
        </Typography>
      </Typography>
      <WithDividers>
        {bookmarks.map((bookmark) => (
          <BookmarkEntry key={bookmark.id} bookmark={bookmark} />
        ))}
      </WithDividers>
    </Box>
  );
};

export const FunctionCallSection = ({ calls }: { calls: FunctionCall[] }) => {
  if (calls.length === 0) return null;

  return (
    <Box>
      <Typography variant="bodyHeavy" sx={{ marginBottom: "8px" }}>
        Function Calls
      </Typography>
      <WithDividers>
        {calls.map((call) => (
          <FunctionCallEntry key={call.id} call={call} />
        ))}
      </WithDividers>
    </Box>
  );
};

// Question Slider Component
const QuestionSlider = ({
  questions,
  onAnswerQuestions,
}: {
  questions: UserQuestion[];
  onAnswerQuestions?: (
    answers: { functionId: string; answer: string }[],
  ) => void;
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [slideDirection, setSlideDirection] = useState<"left" | "right">(
    "right",
  );
  const [currentAnswer, setCurrentAnswer] = useState("");

  const unansweredQuestions = questions.filter((q) => !q.answered);
  const currentQuestion = unansweredQuestions[currentIndex];

  // Only reset when questions array changes (new questions arrive), not when navigating
  useEffect(() => {
    // Initialize answers from existing question answers, but preserve any existing user input
    const existingAnswers: Record<string, string> = {};
    unansweredQuestions.forEach((q) => {
      if (q.answer) {
        existingAnswers[q.id] = q.answer;
      }
    });

    // Merge with existing answers instead of replacing
    setAnswers((prev) => ({ ...existingAnswers, ...prev }));

    // Reset to first question if current index is out of bounds
    if (currentIndex >= unansweredQuestions.length) {
      setCurrentIndex(0);
    }
  }, [questions.length, unansweredQuestions.length]); // Removed currentIndex!

  // Reset current answer when question changes
  useEffect(() => {
    if (currentQuestion) {
      setCurrentAnswer(answers[currentQuestion.id] || "");
    }
  }, [currentQuestion?.id, answers]);

  if (!currentQuestion) return null;

  const handleNext = () => {
    if (currentIndex < unansweredQuestions.length - 1 && currentAnswer.trim()) {
      console.log(
        `Moving to next question. Current answer for ${currentQuestion.id}:`,
        currentAnswer,
      );
      setSlideDirection("right");
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleBack = () => {
    if (currentIndex > 0) {
      setSlideDirection("left");
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleSkip = () => {
    if (currentIndex < unansweredQuestions.length - 1) {
      setSlideDirection("right");
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleAnswer = useCallback(
    (answer: string) => {
      setCurrentAnswer(answer);
      setAnswers((prev) => {
        const newAnswers = { ...prev, [currentQuestion.id]: answer };
        console.log(
          `Answer updated for question ${currentQuestion.id}:`,
          answer,
        );
        console.log("All answers now:", newAnswers);
        return newAnswers;
      });

      // Don't auto-advance - let users navigate manually
    },
    [currentQuestion.id],
  );

  const handleSubmit = () => {
    // Collect all answers and submit them as a batch
    const allAnswers = { ...answers };

    // Make sure we include all questions that have been answered
    const answersArray = unansweredQuestions
      .filter((q) => allAnswers[q.id] && allAnswers[q.id].trim())
      .map((q) => ({
        functionId: q.id,
        answer: allAnswers[q.id],
      }));

    console.log("Submitting answers:", answersArray);
    console.log("All collected answers:", allAnswers);

    if (answersArray.length > 0) {
      onAnswerQuestions?.(answersArray);
    }
  };

  const slideStyles = {
    transform: slideDirection === "right" ? "translateX(0)" : "translateX(0)",
    transition: "transform 0.3s ease-in-out",
  };

  const isLastQuestion = currentIndex === unansweredQuestions.length - 1;

  return (
    <Box
      sx={{
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        padding: "16px",
        backgroundColor: "#f9f9f9",
        overflow: "hidden",
      }}
    >
      {/* Progress indicator */}
      <Box sx={{ display: "flex", alignItems: "center", marginBottom: "16px" }}>
        <Typography variant="bodySmall" color="text.secondary">
          Question {currentIndex + 1} of {unansweredQuestions.length}
        </Typography>
        <Box sx={{ flex: 1, marginLeft: "12px" }}>
          <Box
            sx={{
              width: "100%",
              height: "4px",
              backgroundColor: "#e0e0e0",
              borderRadius: "2px",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                width: `${
                  ((currentIndex + 1) / unansweredQuestions.length) * 100
                }%`,
                height: "100%",
                backgroundColor: "#1976d2",
                transition: "width 0.3s ease-in-out",
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Question content with slide animation */}
      <Box sx={slideStyles}>
        <Typography
          variant="body"
          sx={{ marginBottom: "16px", fontWeight: 500 }}
        >
          {currentQuestion.question}
        </Typography>

        <QuestionAnswerInput
          question={currentQuestion}
          onAnswer={handleAnswer}
          onSkip={handleSkip}
          showNavigation={false}
          currentAnswer={answers[currentQuestion.id] || ""}
        />
      </Box>

      {/* Navigation controls */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "16px",
        }}
      >
        <Box>
          {currentIndex > 0 && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleBack}
              sx={{ marginRight: "8px" }}
            >
              Back
            </Button>
          )}
        </Box>

        <Box>
          {!isLastQuestion && (
            <Button
              variant="text"
              size="small"
              onClick={handleSkip}
              sx={{ marginRight: "8px" }}
            >
              Skip
            </Button>
          )}
          {!isLastQuestion && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleNext}
              disabled={!currentAnswer?.trim()}
            >
              Next
            </Button>
          )}
          {isLastQuestion && (
            <Button
              variant="contained"
              size="small"
              onClick={handleSubmit}
              sx={{
                backgroundColor: "#1976d2",
                "&:hover": { backgroundColor: "#1565c0" },
              }}
            >
              Submit
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export const UserQuestionSection = ({
  questions,
  onAnswerQuestions,
}: {
  questions: UserQuestion[];
  onAnswerQuestions?: (
    answers: { functionId: string; answer: string }[],
  ) => void;
}) => {
  if (questions.length === 0) return null;

  const pendingQuestions = questions.filter((q) => !q.answered);
  const answeredQuestions = questions.filter((q) => q.answered);

  return (
    <Box>
      <Typography variant="bodyHeavy" sx={{ marginBottom: "8px" }}>
        User Questions
        {pendingQuestions.length > 0 && (
          <Typography
            component="span"
            variant="bodySmall"
            sx={{
              marginLeft: "8px",
              backgroundColor: "#fff3cd",
              color: "#856404",
              padding: "2px 6px",
              borderRadius: "4px",
              fontSize: "0.75rem",
            }}
          >
            {pendingQuestions.length} pending
          </Typography>
        )}
      </Typography>

      {/* Use slider for all pending questions */}
      {pendingQuestions.length >= 1 && (
        <QuestionSlider
          questions={pendingQuestions}
          onAnswerQuestions={onAnswerQuestions}
        />
      )}

      {/* Answered Questions */}
      {answeredQuestions.length > 0 && (
        <Box sx={{ marginTop: pendingQuestions.length > 0 ? "16px" : "0" }}>
          {pendingQuestions.length > 0 && (
            <Typography
              variant="bodySmall"
              color="text.secondary"
              sx={{
                marginBottom: "8px",
                fontWeight: 500,
              }}
            >
              Previously Answered ({answeredQuestions.length})
            </Typography>
          )}
          <WithDividers>
            {answeredQuestions.map((question) => (
              <UserQuestionEntry
                key={question.id}
                question={question}
                onAnswerQuestion={(answers) => onAnswerQuestions?.(answers)}
              />
            ))}
          </WithDividers>
        </Box>
      )}
    </Box>
  );
};

export const ChatMessageSection = ({
  messages,
  isCurrentlyActive,
}: {
  messages: string;
  isCurrentlyActive: boolean;
}) => {
  if (!messages) return null;

  // Parse messages to extract XML blocks and render special components
  const parseMessages = (text: string) => {
    const components: Array<{
      type: "text" | "xml";
      content: string;
      componentType?: string;
    }> = [];
    let currentPos = 0;

    // Find XML blocks
    const xmlRegex = /<(\w+)([^>]*?)>/g;
    let match;

    while ((match = xmlRegex.exec(text)) !== null) {
      const tagName = match[1];
      const startPos = match.index;

      // Check if this is a known XML component
      if (XML_COMPONENT_MAP[tagName as keyof typeof XML_COMPONENT_MAP]) {
        // Add text before XML block
        if (startPos > currentPos) {
          components.push({
            type: "text",
            content: text.substring(currentPos, startPos),
          });
        }

        // Find the closing tag
        const closingTag = `</${tagName}>`;
        const endPos = text.indexOf(closingTag, startPos);

        if (endPos !== -1) {
          // Extract the complete XML block
          const xmlBlock = text.substring(startPos, endPos + closingTag.length);
          components.push({
            type: "xml",
            content: xmlBlock,
            componentType: tagName,
          });

          currentPos = endPos + closingTag.length;
          xmlRegex.lastIndex = currentPos;
        } else {
          // If no closing tag found, treat as regular text
          components.push({
            type: "text",
            content: text.substring(currentPos, startPos + match[0].length),
          });
          currentPos = startPos + match[0].length;
        }
      }
    }

    // Add remaining text
    if (currentPos < text.length) {
      components.push({
        type: "text",
        content: text.substring(currentPos),
      });
    }

    return components;
  };

  const renderComponent = (
    component: {
      type: "text" | "xml";
      content: string;
      componentType?: string;
    },
    index: number,
  ) => {
    if (component.type === "xml" && component.componentType) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(component.content, "text/xml");
      const element = doc.documentElement;
      const innerContent = element.textContent || "";

      switch (component.componentType) {
        case "Table":
          return (
            <ChatMessageTable key={index} xmlContent={component.content} />
          );
        case "b":
          return <XmlBold key={index}>{innerContent}</XmlBold>;
        case "i":
          return <XmlItalic key={index}>{innerContent}</XmlItalic>;
        case "h1":
          return <XmlHeading1 key={index}>{innerContent}</XmlHeading1>;
        case "h2":
          return <XmlHeading2 key={index}>{innerContent}</XmlHeading2>;
        case "quote":
          return <XmlQuote key={index}>{innerContent}</XmlQuote>;
        case "a":
          return <XmlLink key={index} xmlContent={component.content} />;
        default:
          return (
            <Typography key={index} variant="body">
              {component.content}
            </Typography>
          );
      }
    }

    return component.content ? (
      <StreamingTextBox
        key={index}
        text={component.content}
        showCursor={
          isCurrentlyActive && index === parseMessages(messages).length - 1
        }
      />
    ) : null;
  };

  const parsedComponents = parseMessages(messages);

  // If no XML components found, fall back to original behavior
  if (parsedComponents.length === 1 && parsedComponents[0].type === "text") {
    return (
      <Box>
        <Typography variant="bodyHeavy" sx={{ marginBottom: "8px" }}>
          Response
        </Typography>
        <StreamingTextBox text={messages} showCursor={isCurrentlyActive} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="bodyHeavy" sx={{ marginBottom: "8px" }}>
        Response
      </Typography>
      <Box
        sx={{
          background: "white",
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          padding: "16px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
        }}
      >
        {parsedComponents.map((component, index) =>
          renderComponent(component, index),
        )}
      </Box>
    </Box>
  );
};

export const ThinkingSection = ({
  thinkingMessage,
  isActive,
}: {
  thinkingMessage: string;
  isActive: boolean;
}) => {
  if (!thinkingMessage || !isActive) return null;

  return (
    <Box>
      <Typography
        variant="bodySmall"
        color="text.secondary"
        sx={{ fontStyle: "italic" }}
      >
        {thinkingMessage}
      </Typography>
    </Box>
  );
};

export const FollowUpInputSection = ({
  onFollowUpQuestion,
}: {
  onFollowUpQuestion?: (question: string) => void;
}) => {
  const [followUpQuestion, setFollowUpQuestion] = useState("");

  const handleSubmit = () => {
    if (followUpQuestion.trim() && onFollowUpQuestion) {
      onFollowUpQuestion(followUpQuestion.trim());
      setFollowUpQuestion("");
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Box>
      <Typography variant="bodyHeavy" sx={{ marginBottom: "8px" }}>
        Follow-up Question
      </Typography>
      <Box
        sx={{
          display: "flex",
          gap: "8px",
          alignItems: "flex-end",
          padding: "12px",
          backgroundColor: "#f8f9fa",
          borderRadius: "8px",
          border: "1px solid #e9ecef",
        }}
      >
        <TextField
          placeholder="Ask a follow-up question..."
          value={followUpQuestion}
          onChange={(e) => setFollowUpQuestion(e.target.value)}
          onKeyDown={handleKeyDown}
          variant="outlined"
          size="small"
          fullWidth
          multiline
          rows={1}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "white",
            },
          }}
        />
        <IconButton
          onClick={handleSubmit}
          disabled={!followUpQuestion.trim()}
          size="small"
          sx={{
            borderRadius: "8px",
            backgroundColor: "#1976d2",
            color: "white",
            padding: "8px",
            "&:hover": {
              backgroundColor: "#1565c0",
            },
            "&:disabled": {
              backgroundColor: "#e0e0e0",
              color: "#9e9e9e",
            },
          }}
        >
          <ArrowUp size={16} />
        </IconButton>
      </Box>
    </Box>
  );
};
